// frontend/src/components/marketplace/CategoryNav.tsx
// Category navigation component with hierarchical display

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Grid3X3,
  Accessibility,
  Home,
  Heart,
  Car,
  Laptop,
  Package,
  Wrench,
  GraduationCap,
  Baby,
} from 'lucide-react';
import { useCategories } from '../hooks/useMarketplace';
import type { VendorCategory } from '../types/marketplace';

interface CategoryNavProps {
  className?: string;
  variant?: 'sidebar' | 'horizontal' | 'dropdown';
  showCounts?: boolean;
  activeCategory?: string;
  onCategorySelect?: (slug: string) => void;
}

// Map category slugs to icons
const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  mobility: Accessibility,
  'home-modifications': Home,
  therapy: Heart,
  vehicles: Car,
  technology: Laptop,
  supplies: Package,
  equipment: Wrench,
  education: GraduationCap,
  pediatric: Baby,
};

const getIconForCategory = (slug: string): React.ComponentType<{ className?: string }> => {
  return CATEGORY_ICONS[slug] || Grid3X3;
};

export const CategoryNav: React.FC<CategoryNavProps> = ({
  className = '',
  variant = 'sidebar',
  showCounts = true,
  activeCategory,
  onCategorySelect,
}) => {
  const location = useLocation();
  const { data: categories, isLoading, error } = useCategories();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Determine active category from URL if not provided
  const currentCategory = activeCategory || location.pathname.split('/category/')[1];

  const toggleCategory = (slug: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const handleCategoryClick = (slug: string) => {
    if (onCategorySelect) {
      onCategorySelect(slug);
    }
  };

  // Build category tree
  const buildCategoryTree = (categories: VendorCategory[]): VendorCategory[] => {
    const categoryMap = new Map<string, VendorCategory & { children: VendorCategory[] }>();
    const roots: (VendorCategory & { children: VendorCategory[] })[] = [];

    // Initialize all categories with empty children array
    categories.forEach((cat) => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Build tree structure
    categories.forEach((cat) => {
      const catWithChildren = categoryMap.get(cat.id)!;
      if (cat.parentId && categoryMap.has(cat.parentId)) {
        categoryMap.get(cat.parentId)!.children.push(catWithChildren);
      } else {
        roots.push(catWithChildren);
      }
    });

    // Sort by display order
    const sortByOrder = (a: VendorCategory, b: VendorCategory) =>
      (a.displayOrder || 0) - (b.displayOrder || 0);

    roots.sort(sortByOrder);
    categoryMap.forEach((cat) => cat.children.sort(sortByOrder));

    return roots;
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <Loader2 className="w-6 h-6 text-sage animate-spin" />
      </div>
    );
  }

  if (error || !categories) {
    return (
      <div className={`text-center py-4 text-warm-gray ${className}`}>
        <p>Unable to load categories</p>
      </div>
    );
  }

  const categoryTree = buildCategoryTree(categories);

  // Sidebar variant
  if (variant === 'sidebar') {
    const renderCategory = (
      category: VendorCategory & { children?: VendorCategory[] },
      depth: number = 0
    ) => {
      const Icon = getIconForCategory(category.slug);
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expandedCategories.has(category.slug);
      const isActive = currentCategory === category.slug;

      return (
        <div key={category.id}>
          <div
            className={`flex items-center gap-2 ${depth > 0 ? 'ml-6' : ''}`}
          >
            {hasChildren ? (
              <button
                onClick={() => toggleCategory(category.slug)}
                className="p-1 hover:bg-cream rounded transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-warm-gray" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-warm-gray" />
                )}
              </button>
            ) : (
              <span className="w-6" />
            )}

            <Link
              to={`/marketplace/category/${category.slug}`}
              onClick={() => handleCategoryClick(category.slug)}
              className={`flex-1 flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-sage text-white'
                  : 'hover:bg-cream text-navy'
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-warm-gray'}`} />
              <span className="flex-1 text-sm font-medium">{category.name}</span>
              {showCounts && category.productCount !== undefined && (
                <span
                  className={`text-xs ${
                    isActive ? 'text-white/70' : 'text-warm-gray'
                  }`}
                >
                  {category.productCount}
                </span>
              )}
            </Link>
          </div>

          {hasChildren && isExpanded && (
            <div className="mt-1 space-y-1">
              {category.children!.map((child) =>
                renderCategory(child as VendorCategory & { children?: VendorCategory[] }, depth + 1)
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <nav className={`space-y-1 ${className}`}>
        <Link
          to="/marketplace"
          onClick={() => handleCategoryClick('')}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
            !currentCategory
              ? 'bg-sage text-white'
              : 'hover:bg-cream text-navy'
          }`}
        >
          <Grid3X3 className={`w-4 h-4 ${!currentCategory ? 'text-white' : 'text-warm-gray'}`} />
          <span className="text-sm font-medium">All Categories</span>
        </Link>
        
        {categoryTree.map((category) =>
          renderCategory(category as VendorCategory & { children?: VendorCategory[] })
        )}
      </nav>
    );
  }

  // Horizontal variant
  if (variant === 'horizontal') {
    return (
      <nav className={`flex items-center gap-2 overflow-x-auto pb-2 ${className}`}>
        <Link
          to="/marketplace"
          className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
            !currentCategory
              ? 'bg-sage text-white'
              : 'bg-cream hover:bg-sage/10 text-navy'
          }`}
        >
          <Grid3X3 className="w-4 h-4" />
          <span className="text-sm font-medium">All</span>
        </Link>

        {categoryTree.map((category) => {
          const Icon = getIconForCategory(category.slug);
          const isActive = currentCategory === category.slug;

          return (
            <Link
              key={category.id}
              to={`/marketplace/category/${category.slug}`}
              onClick={() => handleCategoryClick(category.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-sage text-white'
                  : 'bg-cream hover:bg-sage/10 text-navy'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{category.name}</span>
              {showCounts && category.productCount !== undefined && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20' : 'bg-white'
                  }`}
                >
                  {category.productCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    );
  }

  // Dropdown variant
  if (variant === 'dropdown') {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${className}`}>
        {categoryTree.map((category) => {
          const Icon = getIconForCategory(category.slug);
          const catWithChildren = category as VendorCategory & { children?: VendorCategory[] };

          return (
            <div
              key={category.id}
              className="bg-white rounded-xl p-4 border border-gray-100 hover:border-sage/30 hover:shadow-md transition-all"
            >
              <Link
                to={`/marketplace/category/${category.slug}`}
                onClick={() => handleCategoryClick(category.slug)}
                className="flex items-center gap-3 mb-3"
              >
                <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-sage" />
                </div>
                <div>
                  <h3 className="font-medium text-navy">{category.name}</h3>
                  {showCounts && category.productCount !== undefined && (
                    <p className="text-xs text-warm-gray">
                      {category.productCount} products
                    </p>
                  )}
                </div>
              </Link>

              {catWithChildren.children && catWithChildren.children.length > 0 && (
                <div className="space-y-1 border-t border-gray-100 pt-3">
                  {catWithChildren.children.slice(0, 4).map((child) => (
                    <Link
                      key={child.id}
                      to={`/marketplace/category/${child.slug}`}
                      onClick={() => handleCategoryClick(child.slug)}
                      className="block text-sm text-warm-gray hover:text-sage transition-colors"
                    >
                      {child.name}
                    </Link>
                  ))}
                  {catWithChildren.children.length > 4 && (
                    <Link
                      to={`/marketplace/category/${category.slug}`}
                      className="block text-sm text-sage font-medium"
                    >
                      +{catWithChildren.children.length - 4} more
                    </Link>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

// Breadcrumb navigation for category hierarchy
interface CategoryBreadcrumbProps {
  categorySlug: string;
  categories: VendorCategory[];
  className?: string;
}

export const CategoryBreadcrumb: React.FC<CategoryBreadcrumbProps> = ({
  categorySlug,
  categories,
  className = '',
}) => {
  // Build breadcrumb path
  const buildBreadcrumb = (): VendorCategory[] => {
    const path: VendorCategory[] = [];
    let current = categories.find((c) => c.slug === categorySlug);

    while (current) {
      path.unshift(current);
      if (current.parentId) {
        current = categories.find((c) => c.id === current!.parentId);
      } else {
        break;
      }
    }

    return path;
  };

  const breadcrumb = buildBreadcrumb();

  if (breadcrumb.length === 0) {
    return null;
  }

  return (
    <nav className={`flex items-center gap-2 text-sm ${className}`}>
      <Link to="/marketplace" className="text-warm-gray hover:text-sage transition-colors">
        Marketplace
      </Link>

      {breadcrumb.map((category, index) => (
        <React.Fragment key={category.id}>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          {index === breadcrumb.length - 1 ? (
            <span className="text-navy font-medium">{category.name}</span>
          ) : (
            <Link
              to={`/marketplace/category/${category.slug}`}
              className="text-warm-gray hover:text-sage transition-colors"
            >
              {category.name}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default CategoryNav;

